import type { ISongPeaks } from '../../../entities';
import {
  CustomizableSong,
  FreeSong,
  PaginatedList,
  PaginationData,
} from '../../../entities';
import { Client } from '../../client';
import { SongRepository } from '../../repositories/songRepository';
import { ExternalResourcesRepository } from '../../repositories/externalResourcesRepository';
import { handleErrors } from '../../../helpers';
import {
  SongListResponse,
  QueryParameters,
  MainSongResponse,
} from '../../../responses/responseTypes';

export default class SongRepositoryImpl implements SongRepository {
  protectedClient: Client;

  publicClient: Client;

  externalResourcesRepository: ExternalResourcesRepository;

  constructor(
    protectedClient: Client,
    publicClient: Client,
    externalResourcesRepository: ExternalResourcesRepository
  ) {
    this.protectedClient = protectedClient;
    this.publicClient = publicClient;
    this.externalResourcesRepository = externalResourcesRepository;
  }

  getSong(id: string): Promise<FreeSong | CustomizableSong> {
    const { protectedClient } = this;
    return protectedClient
      .get<MainSongResponse>(`/songs/${id}`)
      .then((response) => {
        const { data } = response;
        if (data.is_free) {
          return new FreeSong(data);
        }
        return new CustomizableSong(data);
      })
      .catch((err) => {
        const error = handleErrors(err);
        throw error;
      });
  }

  async getSongList(
    queryParameters: QueryParameters
  ): Promise<PaginatedList<FreeSong | CustomizableSong>> {
    const { protectedClient } = this;
    return protectedClient
      .get<SongListResponse>('/songs', {
        params: queryParameters,
      })
      .then((response) => {
        const songList: Array<FreeSong | CustomizableSong> =
          response.data.items.map((data) => {
            if (data.is_free) {
              return new FreeSong(data);
            }
            return new CustomizableSong(data);
          });
        const { pagination_data: paginationData } = response.data;

        return new PaginatedList(songList, new PaginationData(paginationData));
      })
      .catch((err) => {
        const error = handleErrors(err);
        throw error;
      });
  }

  getSongPeaksData(jsonUrl: string): Promise<ISongPeaks> {
    const { externalResourcesRepository } = this;
    return externalResourcesRepository.getWaveform(jsonUrl);
  }
}
